/**
 * Premium AJAX product search with live suggestions.
 */
const SELECTORS = {
    root: '[data-product-search]',
    form: '[data-search-form]',
    input: '[data-search-input]',
    panel: '[data-search-panel]',
    status: '[data-search-status]',
    results: '[data-search-results]',
};
let openSearchInstance = null;
export function initSearch() {
    if (!window.distinctLaptopsSearch) {
        return;
    }
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
        ProductSearch.create(root);
    });
}
class ProductSearch {
    static create(root) {
        const form = root.querySelector(SELECTORS.form);
        const input = root.querySelector(SELECTORS.input);
        const panel = root.querySelector(SELECTORS.panel);
        const status = root.querySelector(SELECTORS.status);
        const resultsList = root.querySelector(SELECTORS.results);
        if (!form || !input || !panel || !status || !resultsList) {
            return null;
        }
        return new ProductSearch(root, form, input, panel, status, resultsList);
    }
    constructor(root, form, input, panel, status, resultsList) {
        this.debounceTimer = 0;
        this.abortController = null;
        this.activeIndex = -1;
        this.currentResults = [];
        this.currentTerm = '';
        this.isOpen = false;
        this.onInput = () => {
            this.scheduleSearch();
        };
        this.onFocus = () => {
            if (openSearchInstance && openSearchInstance !== this) {
                openSearchInstance.closePanel();
            }
            openSearchInstance = this;
            const term = this.input.value.trim();
            if (term.length >= this.config.minChars && this.currentResults.length > 0) {
                this.openPanel();
            }
        };
        this.onKeyDown = (event) => {
            if (!this.isOpen) {
                return;
            }
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.moveActive(1);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.moveActive(-1);
                    break;
                case 'Enter':
                    if (this.activeIndex >= 0 && this.currentResults[this.activeIndex]) {
                        event.preventDefault();
                        window.location.href = this.currentResults[this.activeIndex].url;
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    this.closePanel();
                    break;
                default:
                    break;
            }
        };
        this.onDocumentClick = (event) => {
            if (!this.root.contains(event.target)) {
                this.closePanel();
            }
        };
        this.onDocumentKeyDown = (event) => {
            if (event.key === 'Escape' && this.isOpen) {
                event.stopPropagation();
                this.closePanel();
            }
        };
        this.root = root;
        this.config = window.distinctLaptopsSearch;
        this.form = form;
        this.input = input;
        this.panel = panel;
        this.status = status;
        this.resultsList = resultsList;
        this.bindEvents();
    }
    bindEvents() {
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('focus', this.onFocus);
        this.input.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeyDown, true);
    }
    scheduleSearch() {
        window.clearTimeout(this.debounceTimer);
        this.debounceTimer = window.setTimeout(() => {
            void this.runSearch();
        }, this.config.debounceMs);
    }
    async runSearch() {
        const term = this.input.value.trim();
        this.currentTerm = term;
        this.activeIndex = -1;
        if (term.length < this.config.minChars) {
            this.abortController?.abort();
            this.closePanel();
            this.clearResults();
            return;
        }
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.openPanel();
        this.setLoading(true);
        const body = new FormData();
        body.append('action', this.config.action);
        body.append('nonce', this.config.nonce);
        body.append('term', term);
        try {
            const response = await fetch(this.config.ajaxUrl, {
                method: 'POST',
                body,
                signal: this.abortController.signal,
                credentials: 'same-origin',
            });
            if (!response.ok) {
                throw new Error('Search request failed');
            }
            const payload = (await response.json());
            if (this.input.value.trim() !== term) {
                return;
            }
            const results = payload.data?.results ?? [];
            this.currentResults = results;
            this.renderResults(results, term);
        }
        catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }
            this.renderEmpty();
        }
        finally {
            if (this.input.value.trim() === term) {
                this.setLoading(false);
            }
        }
    }
    setLoading(loading) {
        this.panel.setAttribute('aria-busy', loading ? 'true' : 'false');
        this.status.textContent = loading ? this.config.i18n.loading : '';
        this.status.hidden = !loading;
        this.root.classList.toggle('is-loading', loading);
    }
    renderResults(results, term) {
        this.resultsList.innerHTML = '';
        if (results.length === 0) {
            this.renderEmpty();
            return;
        }
        results.forEach((result, index) => {
            const item = document.createElement('li');
            item.className = 'dl-search__item';
            item.setAttribute('role', 'option');
            item.dataset.index = String(index);
            const link = document.createElement('a');
            link.className = 'dl-search__result';
            link.href = result.url;
            link.innerHTML = this.buildResultMarkup(result);
            link.addEventListener('mouseenter', () => {
                this.setActiveIndex(index);
            });
            item.appendChild(link);
            this.resultsList.appendChild(item);
        });
        if (results.length >= this.config.maxResults) {
            const footer = document.createElement('li');
            footer.className = 'dl-search__footer';
            const viewAll = document.createElement('a');
            viewAll.className = 'dl-search__view-all';
            viewAll.href = `${this.form.action}?s=${encodeURIComponent(term)}&post_type=product`;
            viewAll.textContent = this.config.i18n.viewAll;
            footer.appendChild(viewAll);
            this.resultsList.appendChild(footer);
        }
        this.openPanel();
    }
    buildResultMarkup(result) {
        const image = result.image
            ? `<img class="dl-search__thumb" src="${this.escapeAttr(result.image)}" alt="" width="56" height="56" loading="lazy" />`
            : '<span class="dl-search__thumb dl-search__thumb--placeholder" aria-hidden="true"></span>';
        const category = result.category
            ? `<span class="dl-search__category">${this.escapeHtml(result.category)}</span>`
            : '';
        const rating = result.rating > 0
            ? `<span class="dl-search__rating" aria-label="${this.escapeAttr(`Rated ${result.rating} out of 5`)}">
        <span class="dl-search__rating-stars">${this.renderStars(result.rating)}</span>
        <span class="dl-search__rating-value">${result.rating.toFixed(1)}</span>
      </span>`
            : '';
        return `
      ${image}
      <span class="dl-search__meta">
        <span class="dl-search__title">${this.escapeHtml(result.title)}</span>
        ${category}
        <span class="dl-search__bottom">
          <span class="dl-search__price">${result.price_html}</span>
          ${rating}
        </span>
      </span>
    `;
    }
    renderStars(rating) {
        const full = Math.round(rating);
        let stars = '';
        for (let i = 0; i < 5; i += 1) {
            stars += `<span class="dl-search__star${i < full ? ' is-filled' : ''}">★</span>`;
        }
        return stars;
    }
    renderEmpty() {
        this.resultsList.innerHTML = '';
        this.currentResults = [];
        const empty = document.createElement('li');
        empty.className = 'dl-search__empty';
        empty.textContent = this.config.i18n.noResults;
        this.resultsList.appendChild(empty);
        this.openPanel();
    }
    clearResults() {
        this.resultsList.innerHTML = '';
        this.currentResults = [];
        this.status.textContent = '';
        this.status.hidden = true;
    }
    moveActive(direction) {
        if (this.currentResults.length === 0) {
            return;
        }
        const max = this.currentResults.length - 1;
        let next = this.activeIndex + direction;
        if (next < 0) {
            next = max;
        }
        else if (next > max) {
            next = 0;
        }
        this.setActiveIndex(next);
    }
    setActiveIndex(index) {
        this.activeIndex = index;
        this.resultsList.querySelectorAll('.dl-search__item').forEach((item, itemIndex) => {
            const isActive = itemIndex === index;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
    }
    openPanel() {
        this.isOpen = true;
        this.panel.hidden = false;
        this.root.classList.add('is-open');
        this.input.setAttribute('aria-expanded', 'true');
    }
    closePanel() {
        this.isOpen = false;
        this.panel.hidden = true;
        this.root.classList.remove('is-open', 'is-loading');
        this.input.setAttribute('aria-expanded', 'false');
        this.panel.setAttribute('aria-busy', 'false');
        this.activeIndex = -1;
        if (openSearchInstance === this) {
            openSearchInstance = null;
        }
    }
    escapeHtml(value) {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
    escapeAttr(value) {
        return this.escapeHtml(value).replace(/'/g, '&#39;');
    }
}
